import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import {
  CalendarCheck,
  Copy,
  KeyRound,
  Lock,
  Mail,
  MessageCircle,
  Link2,
  Send,
  Unlock,
  UserPlus,
} from "lucide-react";
import { Button } from "@core/components/ui/button";
import { shouldShowGuestAccountNudge } from "./GuestAccountNudge";
import { useToast } from "@core/hooks/use-toast";
import { AuthModal } from "@core/components/modals/AuthModal";
import { pollService, GuestPollClaimError } from "@core/services/PollService";
import {
  buildOwnerAdminUrl,
  getFingerprint,
} from "@core/lib/guestIdentityService";
import { setPendingPollClaim } from "@core/lib/pendingPollClaim";
import { logError } from "@core/lib/error-handling";
import { useOpenFixDate } from "@core/components/final-date/FixDateContext";
import { CtaTracker } from "@core/services/CtaTracker";

/** Cible tactile d'au moins 44 px sur mobile (WCAG 2.5.5) ; taille normale dès `sm`. */
const TAP_TARGET = "min-h-11 sm:min-h-9";

/**
 * Page d'admin invité (spec « utilisable par les IA » §3.3) : actions
 * proposées au créateur SANS COMPTE reconnu par son jeton d'admin
 * (`get_poll_results_access.is_creator`). Pas de bouton « Modifier » : un
 * invité n'a aucune voie d'écriture sur un sondage publié — l'encart l'invite
 * à créer un compte, et la connexion rattache CE sondage (intention explicite
 * posée au clic, consommée après SIGNED_IN).
 *
 * Utilisateur CONNECTÉ muni d'un jeton d'admin valide pour un sondage invité
 * (lien reçu) : même panneau, mais l'encart propose « Rattacher ce sondage à
 * mon compte ». Jamais de rattachement sans ce clic (bug du 28/09).
 *
 * BROUILLON (création anonyme par une IA en mode « brouillon à confirmer »,
 * spec §2 UC2) : le lien d'admin sert de lien de confirmation. Tant que le
 * sondage est en `draft`, le panneau ne montre qu'un récapitulatif en lecture
 * seule et l'action « Publier le sondage » — ni partage (le lien de vote ne
 * mène nulle part), ni clôture. Après publication il reprend son état normal.
 *
 * Au-delà de GUEST_ACCOUNT_NUDGE_RESPONSES réponses, l'encart « Créer un
 * compte » de l'invité s'efface au profit de GuestAccountNudge, affiché en
 * haut de la page de résultats. Rien n'est bloqué ni masqué : c'est ce qui
 * remplace l'ancien plafond dur de 20 participants (2026-09-30).
 */
/** Créneau du récapitulatif de brouillon, libellé déjà formaté par la page. */
export interface GuestOwnerPanelSlot {
  id: string;
  label: string;
}

export interface GuestOwnerPanelProps {
  poll: {
    id: string;
    slug: string;
    title: string;
    status?: string;
    description?: string | null;
  };
  /** Dates / créneaux affichés dans le récapitulatif d'un brouillon. */
  draftSlots?: GuestOwnerPanelSlot[];
  /** Jeton d'admin de CE sondage ; non nul seulement si la page a été ouverte via un lien d'admin. */
  adminTokenFromLink: string | null;
  onStatusChange: (status: "active" | "closed") => void;
  /**
   * Utilisateur connecté + jeton du trousseau pour ce sondage : l'encart
   * propose le rattachement explicite au compte au lieu de l'inscription.
   */
  claimToken?: string | null;
  /** Appelé après un rattachement réussi (la page recharge le sondage). */
  onClaimed?: () => void;
  /** Nombre de réponses compté côté serveur (`get_poll_results_access`). */
  responseCount?: number;
}

export function GuestOwnerPanel({
  poll,
  adminTokenFromLink,
  onStatusChange,
  claimToken = null,
  onClaimed,
  draftSlots = [],
  responseCount = 0,
}: GuestOwnerPanelProps) {
  const { t } = useTranslation(["polls", "errors"]);
  const { toast } = useToast();
  const [busy, setBusy] = useState(false);
  const [authOpen, setAuthOpen] = useState(false);
  const [authMode, setAuthMode] = useState<"signup" | "signin">("signup");
  // L'invitation (GuestAccountNudge) est affichée en haut de la page de
  // résultats par Results : l'encart « Créer un compte » s'efface alors ici,
  // pour ne garder qu'un seul appel à créer un compte.
  const showAccountNudge = shouldShowGuestAccountNudge(
    responseCount,
    claimToken,
  );

  const isClosed = poll.status === "closed";
  const isDraft = poll.status === "draft";
  const origin = typeof window !== "undefined" ? window.location.origin : "";
  const voteUrl = `${origin}/poll/${poll.slug}/vote`;
  const shareMessage = t("polls:guest_admin.share_message", {
    title: poll.title,
    url: voteUrl,
  });

  const copy = async (text: string, successKey: string) => {
    try {
      await navigator.clipboard.writeText(text);
      toast({ title: t(successKey) });
    } catch {
      toast({
        title: t("errors:generic"),
        description: t("polls:results_link_copy_error"),
        variant: "destructive",
      });
    }
  };

  const openFixDate = useOpenFixDate();

  const toggleStatus = async (skipConfirm = false) => {
    const next = isClosed ? "active" : "closed";
    // Clôturer passe désormais par « Fixer la date » (spec S3) ; la fenêtre
    // propose aussi « Clôturer sans fixer de date », qui revient ici.
    if (next === "closed" && openFixDate && !skipConfirm) {
      openFixDate({ onCloseWithoutDate: () => toggleStatus(true) });
      return;
    }
    if (
      next === "closed" &&
      !skipConfirm &&
      !window.confirm(t("polls:guest_admin.close_confirm"))
    ) {
      return;
    }
    setBusy(true);
    try {
      await pollService.updatePoll(poll.id, { status: next });
      onStatusChange(next);
      toast({
        title: t(
          next === "closed"
            ? "polls:guest_admin.closed_toast"
            : "polls:guest_admin.reopened_toast",
        ),
      });
    } catch (error) {
      logError(error, {
        component: "GuestOwnerPanel",
        operation: next === "closed" ? "closePoll" : "reopenPoll",
      });
      toast({
        title: t("errors:generic"),
        description: t("polls:guest_admin.status_error"),
        variant: "destructive",
      });
    } finally {
      setBusy(false);
    }
  };

  const publishDraft = async () => {
    setBusy(true);
    try {
      // `updatePoll` → RPC `set_poll_status` avec le jeton de CE sondage
      // (trousseau, puis jeton principal) : vaut pour l'invité comme pour le
      // connecté muni d'un lien d'admin.
      await pollService.updatePoll(poll.id, { status: "active" });
      onStatusChange("active");
      toast({ title: t("polls:guest_admin.published_toast") });
    } catch (error) {
      logError(error, {
        component: "GuestOwnerPanel",
        operation: "publishDraft",
      });
      toast({
        title: t("errors:generic"),
        description: t("polls:guest_admin.publish_error"),
        variant: "destructive",
      });
    } finally {
      setBusy(false);
    }
  };

  const claimToAccount = async () => {
    if (!claimToken) return;
    setBusy(true);
    try {
      const fingerprint = await getFingerprint();
      await pollService.claimGuestPollWithToken(
        poll.id,
        claimToken,
        fingerprint,
        poll.slug,
      );
      toast({ title: t("polls:guest_admin.claimed_toast") });
      onClaimed?.();
    } catch (error) {
      const rateLimited =
        error instanceof GuestPollClaimError && error.reason === "rate_limited";
      if (!rateLimited) {
        logError(error, {
          component: "GuestOwnerPanel",
          operation: "claimGuestPoll",
        });
      }
      toast({
        title: t("errors:generic"),
        description: t(
          rateLimited
            ? "polls:guest_admin.claim_rate_limited"
            : "polls:guest_admin.claim_error",
        ),
        variant: "destructive",
      });
    } finally {
      setBusy(false);
    }
  };

  const openAuth = (mode: "signup" | "signin") => {
    // Rattachement explicite de CE sondage après connexion (consommé par
    // AuthContext au SIGNED_IN) — la connexion seule ne rattache plus rien.
    setPendingPollClaim(poll.id, poll.slug);
    // Retour sur cette page après connexion (AuthModal / OAuth).
    try {
      localStorage.setItem(
        "auth_return_to",
        `/poll/${encodeURIComponent(poll.slug)}/results`,
      );
    } catch {
      // Stockage indisponible : l'utilisateur reviendra à la main.
    }
    setAuthMode(mode);
    setAuthOpen(true);
  };

  return (
    <section
      className="my-4 space-y-3 min-w-0"
      data-testid="guest-owner-panel"
      aria-label={t("polls:guest_admin.panel_title")}
    >
      {adminTokenFromLink && (
        <div
          className="rounded-lg border border-warning bg-warning-bg p-3 flex flex-col sm:flex-row sm:items-center gap-2 min-w-0"
          data-testid="guest-admin-keep-link"
          role="status"
        >
          <KeyRound className="h-5 w-5 shrink-0 text-warning" />
          <p className="flex-1 min-w-0 text-sm text-foreground break-words">
            {t("polls:guest_admin.keep_link")}
          </p>
          <Button
            type="button"
            size="sm"
            variant="outline"
            className={`shrink-0 ${TAP_TARGET}`}
            data-testid="guest-admin-copy-admin-link"
            onClick={() =>
              void copy(
                buildOwnerAdminUrl(origin, poll.slug, adminTokenFromLink),
                "polls:guest_admin.admin_link_copied",
              )
            }
          >
            <Copy className="h-4 w-4 mr-2" />
            {t("polls:guest_admin.copy_admin_link")}
          </Button>
        </div>
      )}

      {isDraft ? (
        <div
          className="rounded-lg border border-primary/40 bg-card p-4 space-y-4 min-w-0"
          data-testid="guest-admin-draft"
        >
          <div className="space-y-1 min-w-0">
            <h3 className="text-base font-semibold text-foreground">
              {t("polls:guest_admin.draft_title")}
            </h3>
            <p
              className="text-sm text-muted-foreground break-words"
              data-testid="guest-admin-status"
            >
              {t("polls:guest_admin.draft_explanation")}
            </p>
          </div>

          <dl
            className="rounded-md border border-border bg-background p-3 space-y-3 text-sm min-w-0"
            data-testid="guest-admin-draft-recap"
          >
            <div className="min-w-0">
              <dt className="text-xs font-medium text-muted-foreground">
                {t("polls:guest_admin.draft_recap_title")}
              </dt>
              <dd className="text-foreground font-medium break-words">
                {poll.title}
              </dd>
            </div>
            {poll.description?.trim() && (
              <div className="min-w-0">
                <dt className="text-xs font-medium text-muted-foreground">
                  {t("polls:guest_admin.draft_recap_description")}
                </dt>
                <dd className="text-foreground break-words whitespace-pre-line max-h-32 overflow-y-auto">
                  {poll.description}
                </dd>
              </div>
            )}
            <div className="min-w-0">
              <dt className="text-xs font-medium text-muted-foreground">
                {t("polls:guest_admin.draft_recap_slots", {
                  count: draftSlots.length,
                })}
              </dt>
              <dd className="min-w-0">
                {draftSlots.length === 0 ? (
                  <span className="text-muted-foreground">
                    {t("polls:guest_admin.draft_recap_no_slots")}
                  </span>
                ) : (
                  // Liste bornée : jusqu'à 31 dates × 6 créneaux côté MCP.
                  <ul
                    className="max-h-48 overflow-y-auto flex flex-wrap gap-1.5 pt-1"
                    data-testid="guest-admin-draft-slots"
                  >
                    {draftSlots.map((slot) => (
                      <li
                        key={slot.id}
                        className="rounded-md border border-border bg-card px-2 py-1 text-xs text-foreground break-words max-w-full"
                      >
                        {slot.label}
                      </li>
                    ))}
                  </ul>
                )}
              </dd>
            </div>
          </dl>

          <Button
            type="button"
            // Un brouillon sans aucune date ne se publie pas : personne ne
            // pourrait y répondre.
            disabled={busy || draftSlots.length === 0}
            onClick={() => void publishDraft()}
            data-testid="guest-admin-publish"
            className={`w-full sm:w-auto ${TAP_TARGET}`}
          >
            <Send className="h-4 w-4 mr-2" />
            {t("polls:guest_admin.publish_poll")}
          </Button>
        </div>
      ) : (
        <div className="rounded-lg border border-border bg-card p-4 space-y-4 min-w-0">
          <div className="flex flex-col sm:flex-row sm:items-center gap-3 min-w-0">
            <p
              className="flex-1 min-w-0 text-sm text-muted-foreground break-words"
              data-testid="guest-admin-status"
            >
              {t(
                isClosed
                  ? "polls:guest_admin.status_closed"
                  : "polls:guest_admin.status_active",
              )}
            </p>
            <Button
              type="button"
              variant={isClosed ? "default" : "outline"}
              disabled={busy}
              onClick={() => void toggleStatus()}
              data-testid={
                isClosed ? "guest-admin-reopen" : "guest-admin-close"
              }
              className={`shrink-0 ${TAP_TARGET}`}
            >
              {isClosed ? (
                <Unlock className="h-4 w-4 mr-2" />
              ) : openFixDate ? (
                <CalendarCheck className="h-4 w-4 mr-2" />
              ) : (
                <Lock className="h-4 w-4 mr-2" />
              )}
              {t(
                isClosed
                  ? "polls:guest_admin.reopen_poll"
                  : openFixDate
                    ? "polls:final_date.fix_date_action"
                    : "polls:guest_admin.close_poll",
              )}
            </Button>
          </div>

          <div className="space-y-2 min-w-0">
            <h3 className="text-sm font-semibold text-foreground">
              {t("polls:guest_admin.share_title")}
            </h3>
            <div className="flex flex-wrap gap-2">
              <Button
                type="button"
                size="sm"
                variant="outline"
                className={TAP_TARGET}
                data-testid="guest-admin-copy-vote-link"
                onClick={() => {
                  CtaTracker.trackShared(poll.id, "copy");
                  void copy(voteUrl, "polls:guest_admin.vote_link_copied");
                }}
              >
                <Copy className="h-4 w-4 mr-2" />
                {t("polls:guest_admin.copy_vote_link")}
              </Button>
              <Button
                type="button"
                size="sm"
                variant="outline"
                className={TAP_TARGET}
                asChild
              >
                <a
                  href={`https://wa.me/?text=${encodeURIComponent(shareMessage)}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="guest-admin-share-whatsapp"
                  onClick={() => CtaTracker.trackShared(poll.id, "whatsapp")}
                >
                  <MessageCircle className="h-4 w-4 mr-2" />
                  {t("polls:guest_admin.share_whatsapp")}
                </a>
              </Button>
              <Button
                type="button"
                size="sm"
                variant="outline"
                className={TAP_TARGET}
                asChild
              >
                <a
                  href={`mailto:?subject=${encodeURIComponent(
                    t("polls:guest_admin.share_email_subject", {
                      title: poll.title,
                    }),
                  )}&body=${encodeURIComponent(shareMessage)}`}
                  data-testid="guest-admin-share-email"
                >
                  <Mail className="h-4 w-4 mr-2" />
                  {t("polls:guest_admin.share_email")}
                </a>
              </Button>
            </div>
          </div>
        </div>
      )}

      {claimToken ? (
        <div
          className="rounded-lg border border-primary/30 bg-primary/5 p-4 flex flex-col sm:flex-row sm:items-center gap-3 min-w-0"
          data-testid="guest-admin-claim-cta"
        >
          <p className="flex-1 min-w-0 text-sm text-foreground break-words">
            {t("polls:guest_admin.claim_title")}
          </p>
          <Button
            type="button"
            className={`shrink-0 ${TAP_TARGET}`}
            disabled={busy}
            onClick={() => void claimToAccount()}
            data-testid="guest-admin-claim-button"
          >
            <Link2 className="h-4 w-4 mr-2" />
            {t("polls:guest_admin.claim_cta")}
          </Button>
        </div>
      ) : showAccountNudge ? null : (
        <div
          className="rounded-lg border border-primary/30 bg-primary/5 p-4 flex flex-col sm:flex-row sm:items-center gap-3 min-w-0"
          data-testid="guest-admin-account-cta"
        >
          <p className="flex-1 min-w-0 text-sm text-foreground break-words">
            {t("polls:guest_admin.account_title")}
          </p>
          <Button
            type="button"
            className={`shrink-0 ${TAP_TARGET}`}
            onClick={() => openAuth("signup")}
          >
            <UserPlus className="h-4 w-4 mr-2" />
            {t("polls:guest_admin.account_cta")}
          </Button>
        </div>
      )}

      {!claimToken && (
        <AuthModal
          key={authMode}
          open={authOpen}
          onOpenChange={setAuthOpen}
          defaultMode={authMode}
        />
      )}
    </section>
  );
}
